import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowUpRight, Map, AlertTriangle } from 'lucide-react';
import { ConversationThread } from '../components/admin/support/ConversationThread';
import { ReplyComposer } from '../components/admin/support/ReplyComposer';
import { TicketDetailSidebar } from '../components/admin/support/TicketDetailSidebar';
import { TicketStatusPill } from '../components/admin/support/TicketStatusPill';
import { TicketPriorityPill } from '../components/admin/support/TicketPriorityPill';
import { BRAND, SOURCE_LABELS, CATEGORY_LABELS, type TicketStatus, type TicketPriority } from '../components/admin/support/statuses';
import {
  fetchTicketDetail,
  updateTicket,
  sendReply,
  addNote,
  type TicketDetail,
} from '../components/admin/support/ticketApi';
import { RoadmapEditorModal } from '../components/admin/RoadmapEditorModal';
import {
  adminRoadmapService,
  type AdminRoadmapItem,
  type AdminCompositionResponse,
  type RoadmapCategory,
} from '../services/adminRoadmapService';

export const AdminSupportTicket: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [savingField, setSavingField] = useState<string | null>(null);

  // Inline subject editor — admins can click the title to rename the
  // ticket, which becomes the email subject for all future replies
  // (build_reply_subject re-derives "Re: <subject> [#N]" on every send).
  const [editingSubject, setEditingSubject] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState('');
  // Guards against the second commit triggered when `disabled` blurs
  // the focused input mid-save.
  const subjectCommittingRef = useRef(false);

  // Promote-to-Roadmap state. linkedRoadmapItem is set when the ticket
  // has already been promoted (we show "Already on Roadmap →" instead of
  // the Promote button). promoteOpen drives the modal visibility.
  const [linkedRoadmapItem, setLinkedRoadmapItem] = useState<AdminRoadmapItem | null>(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [composition, setComposition] = useState<AdminCompositionResponse | null>(null);

  const load = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      setErr(null);
      const t = await fetchTicketDetail(ticketId);
      setTicket(t);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Clear prior ticket state immediately when navigating to a different ID
  // so we don't flash ticket A's contents while ticket B is fetching.
  useEffect(() => {
    setTicket(null);
    setErr(null);
    setLinkedRoadmapItem(null);
    load();
  }, [ticketId, load]);

  // Check whether this ticket is already promoted to a roadmap item. If
  // yes, we swap the Promote button for a link to the existing item.
  useEffect(() => {
    if (!ticketId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await adminRoadmapService.getSourceForTicket(ticketId);
        if (!cancelled) setLinkedRoadmapItem(res.item);
      } catch (e) {
        // Silent — Promote button just stays visible if the lookup fails
      }
    })();
    return () => { cancelled = true; };
  }, [ticketId]);

  // Pull composition once for the 80/20 warning inside the modal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await adminRoadmapService.composition();
        if (!cancelled) setComposition(c);
      } catch {
        // Silent — warning just won't fire
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Derive pre-fill values from the ticket for the Promote modal. Title
  // comes from the subject; body from the first inbound (requester)
  // message. Category is guessed from the ticket's category field —
  // "feature_idea" → feature, "problem" → known_issue (admin can change).
  const promotePrefill = useMemo(() => {
    if (!ticket) return undefined;
    const firstInbound = ticket.messages.find((m) => m.direction === 'inbound');
    const guessCategory = (c: string | null): RoadmapCategory => {
      if (c === 'feature_idea') return 'feature';
      if (c === 'problem') return 'known_issue';
      return 'improvement';
    };
    return {
      title: ticket.subject ?? '',
      body: (firstInbound?.body_text ?? '').trim(),
      category: guessCategory(ticket.category),
    };
  }, [ticket]);

  const handleUpdate = useCallback(
    async (payload: {
      status?: TicketStatus;
      priority?: TicketPriority;
      assigned_admin_id?: string | null;
      clear_assignee?: boolean;
      tags?: string[];
      subject?: string;
    }) => {
      if (!ticketId) return;
      const firstKey = Object.keys(payload)[0] || 'field';
      setSavingField(firstKey);
      try {
        await updateTicket(ticketId, payload as any);
        await load();
      } catch (e: any) {
        console.error('Update failed', e);
        alert(`Update failed: ${e?.message || 'unknown error'}`);
      } finally {
        setSavingField(null);
      }
    },
    [ticketId, load]
  );

  const startEditSubject = useCallback(() => {
    setSubjectDraft(ticket?.subject ?? '');
    setEditingSubject(true);
  }, [ticket?.subject]);

  const cancelEditSubject = useCallback(() => {
    setEditingSubject(false);
    setSubjectDraft('');
  }, []);

  const commitSubject = useCallback(async () => {
    if (subjectCommittingRef.current) return;
    const next = subjectDraft.trim();
    const current = (ticket?.subject ?? '').trim();
    if (next === current) {
      cancelEditSubject();
      return;
    }
    if (!next) {
      alert('Subject cannot be empty');
      return;
    }
    subjectCommittingRef.current = true;
    try {
      await handleUpdate({ subject: next });
    } finally {
      subjectCommittingRef.current = false;
    }
    cancelEditSubject();
  }, [subjectDraft, ticket?.subject, handleUpdate, cancelEditSubject]);

  const handleSendReply = useCallback(
    async (body: string) => {
      if (!ticketId) return;
      await sendReply(ticketId, body);
      await load();
    },
    [ticketId, load]
  );

  const handleAddNote = useCallback(
    async (body: string) => {
      if (!ticketId) return;
      await addNote(ticketId, body);
      await load();
    },
    [ticketId, load]
  );

  if (loading && !ticket) {
    return <LoadingView />;
  }

  if (err || !ticket) {
    return <ErrorView message={err || 'Ticket not found'} onBack={() => navigate('/admin/support')} />;
  }

  const src = SOURCE_LABELS[ticket.source];
  const cat = ticket.category ? CATEGORY_LABELS[ticket.category] : undefined;

  return (
    <div
      style={{
        marginLeft: -24,
        marginRight: -24,
        marginTop: -24,
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          background: BRAND.surface,
          borderBottom: `1px solid ${BRAND.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => navigate('/admin/support')}
            style={{
              padding: '6px 12px',
              background: 'transparent',
              border: `1px solid ${BRAND.border}`,
              borderRadius: 6,
              color: BRAND.textSecondary,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ← Inbox
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: BRAND.textTertiary, fontVariantNumeric: 'tabular-nums' }}>
                Ticket #{ticket.ticket_number}
              </span>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 100,
                  fontSize: 10,
                  fontWeight: 700,
                  background: src?.bg,
                  color: src?.text,
                }}
              >
                {src?.label || ticket.source}
              </span>
              {cat && (
                <span style={{ fontSize: 12, color: BRAND.textTertiary, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <cat.icon size={13} />{cat.label}
                </span>
              )}
            </div>
            {editingSubject ? (
              <input
                autoFocus
                value={subjectDraft}
                onChange={(e) => setSubjectDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitSubject();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEditSubject();
                  }
                }}
                onBlur={commitSubject}
                disabled={savingField === 'subject'}
                maxLength={500}
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: BRAND.text,
                  margin: '4px 0 0 0',
                  padding: '2px 6px',
                  border: `1px solid ${BRAND.green}`,
                  borderRadius: 6,
                  background: '#fff',
                  width: 'min(680px, 80vw)',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
            ) : (
              <h1
                onClick={startEditSubject}
                title="Click to edit subject"
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: BRAND.text,
                  margin: '4px 0 0 0',
                  padding: '2px 6px',
                  marginLeft: -6,
                  borderRadius: 6,
                  cursor: 'text',
                  display: 'inline-block',
                  maxWidth: 'min(680px, 80vw)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = BRAND.surfaceAlt;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                {ticket.subject || '(no subject)'}
              </h1>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {linkedRoadmapItem ? (
            <button
              onClick={() => navigate('/admin/roadmap')}
              title={`Linked: ${linkedRoadmapItem.title}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                background: 'rgba(31,108,79,0.08)',
                border: `1px solid rgba(31,108,79,0.25)`,
                borderRadius: 8,
                color: BRAND.green,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                maxWidth: 260,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              <Map size={13} />
              On Roadmap
              <ArrowUpRight size={12} />
            </button>
          ) : (
            <button
              onClick={() => setPromoteOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                background: BRAND.green,
                border: 'none',
                borderRadius: 8,
                color: 'var(--surface)',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <Map size={13} />
              Promote to Roadmap
            </button>
          )}
          <TicketPriorityPill priority={ticket.priority} />
          <TicketStatusPill status={ticket.status} />
        </div>
      </div>

      {/* Main workspace: conversation + sidebar */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, background: BRAND.surfaceAlt }}>
        {/* Center: conversation + composer */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            padding: '24px 32px',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 180px)',
          }}
        >
          <div style={{ maxWidth: 820, width: '100%', margin: '0 auto' }}>
            <ConversationThread messages={ticket.messages} />

            <div style={{ marginTop: 28 }}>
              <ReplyComposer
                onSendReply={handleSendReply}
                onAddNote={handleAddNote}
                requesterEmail={ticket.requester.email}
                disabledReason={
                  !ticket.requester.email && ticket.status !== 'closed'
                    ? 'No email on file — reply disabled'
                    : undefined
                }
              />
            </div>
          </div>
        </div>

        {/* Right sidebar */}
        <TicketDetailSidebar ticket={ticket} onUpdate={handleUpdate} savingField={savingField} />
      </div>

      {/* Promote-to-Roadmap modal — renders when admin clicks the button. */}
      {promoteOpen && ticketId && (
        <RoadmapEditorModal
          promoteFromTicketId={ticketId}
          prefill={promotePrefill}
          composition={composition}
          onClose={() => setPromoteOpen(false)}
          onSaved={(saved) => {
            setPromoteOpen(false);
            if (saved) setLinkedRoadmapItem(saved);
          }}
        />
      )}
    </div>
  );
};

const LoadingView: React.FC = () => (
  <div style={{ padding: 60, textAlign: 'center', color: BRAND.textMuted, fontSize: 14 }}>
    Loading ticket…
  </div>
);

const ErrorView: React.FC<{ message: string; onBack: () => void }> = ({ message, onBack }) => (
  <div style={{ padding: 60, textAlign: 'center' }}>
    <div style={{ marginBottom: 12 }}><AlertTriangle size={32} style={{ color: 'var(--status-warning)' }} /></div>
    <div style={{ fontSize: 15, fontWeight: 600, color: BRAND.text, marginBottom: 8 }}>
      Could not load ticket
    </div>
    <div style={{ fontSize: 13, color: BRAND.textMuted, marginBottom: 20 }}>{message}</div>
    <button
      onClick={onBack}
      style={{
        padding: '8px 20px',
        background: BRAND.green,
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      ← Back to inbox
    </button>
  </div>
);
